/**
 * PATH: packages/ui/src/kanban/components/card-detail/commentDraft.ts
 * FILE_ID: UI-KANBAN-CMP-COMMENT-DRAFT-001
 * PURPOSE: Local-only, per-user + per-card draft persistence for the card
 *          COMMENT / notes composer(s) — drawer Card notes, peek Thread
 *          comments, and card-face quick-capture. Restores in-progress text
 *          when an operator navigates away / hovers a different card / closes
 *          the peek before sending, then returns to the SAME card.
 * WHY: FEAT-KANBAN-COMMENT-DRAFTS — a half-typed comment lost on a stray
 *      hover-away is pure operator frustration. Drafts never touch the
 *      server (device-local), and are scoped by BOTH viewer id AND card id
 *      so a shared browser profile never leaks one user's draft to another,
 *      and a draft never bleeds across cards.
 *
 *      HARDENING (FEAT-KANBAN-COMMENT-DRAFTS Phase 2): records are now
 *      timestamped `{ text, ts }` so a lazy sweep can (a) purge drafts older
 *      than DRAFT_TTL_MS — otherwise a draft on a since-deleted/archived card
 *      would orphan in localStorage forever — and (b) cap the total at
 *      MAX_DRAFTS, evicting the oldest first. Writes are debounced at the hook
 *      so fast typing doesn't hammer synchronous setItem. Reads are
 *      back-compatible with the pre-Phase-2 bare-string values.
 *
 *      HARDENING (Phase 3): flush pending writes on identity change +
 *      pagehide/visibilitychange; migrate anon→real user drafts so auth
 *      hydration doesn't orphan typed text under the 'anon' key.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────────────────────┐   ┌──────────┐
 *   │ userId +      │ → │ localStorage                      │ → │ {text,ts}│
 *   │ cardId + text │   │ kanban:comment-draft:<seg>:<card> │   │ + sweep  │
 *   └──────────────┘   └───────────────────────────────────┘   └──────────┘
 * DEPENDENCIES:
 *   - React useSyncExternalStore: tear-free reads that survive remount and
 *     stay in sync across every mounted composer for the same card IN THIS
 *     TAB (the cross-tab `storage` subscription was deliberately dropped —
 *     see useCommentDraft).
 * RELATED:
 *   - openModePref.ts: the sibling storage+useSyncExternalStore pattern this
 *     copies (userSegment 'anon' fallback, try/catch guards). NOTE:
 *     openModePref is a shared PREFERENCE — it intentionally syncs across tabs.
 *     Drafts are the opposite: per-card EPHEMERAL, restore-on-mount content, so
 *     cross-tab sync here only risked one tab clobbering another mid-type.
 *   - KanbanCardPeekComments.tsx / CommentsSection.tsx /
 *     KanbanCardQuickCapture.tsx: the three card-notes composers (shared key).
 *   - CardTeamsThreadSection.tsx: OUT OF SCOPE — Team chat stays no-drafts.
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

/** Cross-mount notify channel (same tab only). `storage` events don't fire in
 *  the writing tab, so we dispatch our own to repaint every mounted composer
 *  of the same card in THIS tab. We deliberately do NOT listen for `storage`
 *  (other tabs) — see useCommentDraft. */
const CHANGE_EVENT = 'kanban:comment-draft-changed'

/** Namespace prefix — the sweep only ever iterates keys under this prefix so a
 *  co-tenant of localStorage (openModePref, app caches, …) is never touched. */
const KEY_PREFIX = 'kanban:comment-draft:'

/** Drafts idle for longer than this are swept. 14 days is long enough to cover
 *  "I'll finish that note after the weekend" yet short enough that a draft on a
 *  card someone deleted/archived can't orphan in localStorage indefinitely. */
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000

/** Hard cap on how many drafts we keep. Past this the oldest-by-`ts` are
 *  evicted first (LRU). A power user rarely has more than a handful of
 *  in-flight drafts; the cap just stops a pathological accumulation from
 *  filling the (5-10 MB) localStorage quota. */
const MAX_DRAFTS = 50

/** Debounce window for keystroke-driven writes. Long enough that a burst of
 *  typing collapses into ONE synchronous setItem, short enough that a
 *  hover-away a moment after typing still catches the latest text. */
const DEBOUNCE_MS = 300

interface DraftRecord {
  text: string
  ts: number
}

/** BUG-2026-07-03 parity with openModePref: identity absence must degrade to a
 *  device-scoped draft, NEVER a dead/no-op composer. Hosts whose auth bridge
 *  hydrates `user: null` still get a working, restorable draft under 'anon'. */
function userSegment(userId: string | number | null | undefined): string {
  return userId === null || userId === undefined || userId === ''
    ? 'anon'
    : String(userId)
}

/** Stable scope string for composer body reseed — changes when cardId OR the
 *  draft-key user segment changes (not merely when a new object identity
 *  arrives for the same user). */
export function commentDraftScope(
  userId: string | number | null | undefined,
  cardId: string,
): string {
  return `${userSegment(userId)}:${cardId}`
}

/** Per-user + per-card key. The `<seg>` segment is the privacy boundary: a
 *  different viewer id yields a different key, so drafts are never visible to
 *  another user even on a shared profile; the `<cardId>` segment keeps drafts
 *  from bleeding across cards. Back-compat: same shape as Phase 1/2
 *  (`kanban:comment-draft:<seg>:<cardId>`) — the card-notes family (drawer,
 *  peek, quick-capture) all share this key so typing continues across surfaces. */
function storageKey(
  userId: string | number | null | undefined,
  cardId: string,
): string {
  return `${KEY_PREFIX}${userSegment(userId)}:${cardId}`
}

/** Decode a stored value into a record. BACK-COMPAT: a pre-Phase-2 value was a
 *  bare string with no timestamp — treat it as a fresh record (ts = now) so a
 *  legacy draft reads correctly and isn't instantly swept. Returns null for a
 *  missing/blank/corrupt value. */
function decodeDraft(raw: string | null): DraftRecord | null {
  if (raw === null || raw === '') return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as { text?: unknown }).text === 'string'
    ) {
      const rec = parsed as { text: string; ts?: unknown }
      return {
        text: rec.text,
        ts: typeof rec.ts === 'number' ? rec.ts : Date.now(),
      }
    }
  } catch {
    /* Not JSON → a legacy bare-string draft. Fall through. */
  }
  // Legacy bare string (or JSON that isn't our shape): treat verbatim as fresh.
  return { text: raw, ts: Date.now() }
}

/** Lazy maintenance: purge expired drafts and enforce the LRU cap. Runs on
 *  write (cheap — one pass over the handful of draft keys). Only ever touches
 *  keys under KEY_PREFIX. Snapshots the keys BEFORE removing any so we never
 *  mutate localStorage while iterating it by index. */
function sweepDrafts(now: number): void {
  if (typeof window === 'undefined') return
  try {
    const store = window.localStorage
    const found: { key: string; ts: number }[] = []
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (key === null || !key.startsWith(KEY_PREFIX)) continue
      const rec = decodeDraft(store.getItem(key))
      if (rec === null) continue
      found.push({ key, ts: rec.ts })
    }
    const survivors: { key: string; ts: number }[] = []
    for (const entry of found) {
      if (now - entry.ts > DRAFT_TTL_MS) store.removeItem(entry.key)
      else survivors.push(entry)
    }
    if (survivors.length > MAX_DRAFTS) {
      // Oldest first, evict the overflow.
      survivors.sort((a, b) => a.ts - b.ts)
      const overflow = survivors.length - MAX_DRAFTS
      for (let i = 0; i < overflow; i++) store.removeItem(survivors[i].key)
    }
  } catch {
    /* quota / disabled storage — nothing to sweep, ignore. */
  }
}

export function readCommentDraft(
  userId: string | number | null | undefined,
  cardId: string,
): string {
  if (typeof window === 'undefined') return ''
  try {
    const rec = decodeDraft(window.localStorage.getItem(storageKey(userId, cardId)))
    if (rec === null) return ''
    // An expired record reads as "no draft" (the sweep will remove it on the
    // next write); we don't delete on read to keep reads side-effect-free.
    if (Date.now() - rec.ts > DRAFT_TTL_MS) return ''
    return rec.text
  } catch {
    return ''
  }
}

/** Persist `text` for this user+card as a timestamped record. Writing an empty
 *  string DELETES the key (an empty draft is no draft) so the store never
 *  accumulates stale blanks. Runs the lazy sweep after every write. */
export function writeCommentDraft(
  userId: string | number | null | undefined,
  cardId: string,
  text: string,
): void {
  if (typeof window === 'undefined') return
  const now = Date.now()
  try {
    const key = storageKey(userId, cardId)
    if (text === '') {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, JSON.stringify({ text, ts: now }))
    }
    sweepDrafts(now)
  } catch {
    /* quota / disabled storage — the event below still syncs this session's
       mounted composers; the draft just won't survive a reload. */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/** Explicit purge — used on a successful send and by the manual "Clear draft"
 *  affordance. Identical to writing '' but reads as intent at the call site.
 *  Immediate (never debounced) so a clear always wins over an in-flight write. */
export function clearCommentDraft(
  userId: string | number | null | undefined,
  cardId: string,
): void {
  writeCommentDraft(userId, cardId, '')
}

/**
 * When auth hydrates `null/anon → realId`, move this card's anon draft onto
 * the user key (only if the user key is empty) and drop the anon key so typed
 * text isn't orphaned under 'anon'. Never overwrites an existing user draft.
 *
 * Silent (no CHANGE_EVENT): safe to call during render before a composer
 * reseeds from `readCommentDraft`, so the same frame sees the migrated text.
 */
export function migrateAnonCommentDraft(
  userId: string | number | null | undefined,
  cardId: string,
): void {
  if (typeof window === 'undefined') return
  if (userSegment(userId) === 'anon') return
  try {
    const anonKey = storageKey(null, cardId)
    const userKey = storageKey(userId, cardId)
    const anonRec = decodeDraft(window.localStorage.getItem(anonKey))
    if (anonRec === null || anonRec.text === '') return
    if (Date.now() - anonRec.ts > DRAFT_TTL_MS) {
      window.localStorage.removeItem(anonKey)
      return
    }
    const userRec = decodeDraft(window.localStorage.getItem(userKey))
    const userEmpty =
      userRec === null ||
      userRec.text === '' ||
      Date.now() - userRec.ts > DRAFT_TTL_MS
    if (userEmpty) {
      window.localStorage.setItem(
        userKey,
        JSON.stringify({ text: anonRec.text, ts: Date.now() }),
      )
    }
    window.localStorage.removeItem(anonKey)
  } catch {
    /* quota / disabled storage — ignore. */
  }
}

export interface UseCommentDraft {
  draft: string
  setDraft: (text: string) => void
  clearDraft: () => void
}

type PendingWrite = {
  userId: string | number | null | undefined
  cardId: string
  text: string
}

/**
 * Draft text backed by localStorage + a same-tab change event. Survives remount
 * (the composer unmounts when the peek/drawer closes) and keeps every mounted
 * composer for the SAME card in THIS tab in sync. `userId = null/undefined`
 * scopes the draft to the device-shared `anon` segment — restorable, never dead.
 *
 * Writes are DEBOUNCED (DEBOUNCE_MS) so a burst of typing collapses into one
 * synchronous setItem. `clearDraft` is IMMEDIATE and cancels any pending
 * debounced write first, so a "clear" (send / manual Clear) can never be
 * resurrected by a late write. A still-pending write is FLUSHED on unmount,
 * when cardId/userId identity changes, and on pagehide/visibility hidden so a
 * half-typed draft still survives hover-away and best-effort refresh.
 */
export function useCommentDraft(
  userId: string | number | null | undefined,
  cardId: string,
): UseCommentDraft {
  // Sync migrate BEFORE the snapshot read: composers reseed `body` from
  // `draft` in the same render that userId flips anon→real. An effect-only
  // migrate would leave that reseed reading an empty user key.
  migrateAnonCommentDraft(userId, cardId)

  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === 'undefined') return () => {}
    // Same-tab only. We intentionally DON'T subscribe to `storage` (other
    // tabs): a draft is per-card ephemeral, restore-on-mount content, not a
    // shared preference like openModePref, so a second tab syncing in mid-type
    // would clobber what the operator is actively writing here.
    window.addEventListener(CHANGE_EVENT, onChange)
    return () => window.removeEventListener(CHANGE_EVENT, onChange)
  }, [])
  const getSnapshot = useCallback(
    () => readCommentDraft(userId, cardId),
    [userId, cardId],
  )
  const draft = useSyncExternalStore(subscribe, getSnapshot, () => '')

  // Debounced-write bookkeeping. `pendingRef` carries the identity captured at
  // setDraft time so a write always lands on the card it was typed for, even if
  // `cardId` changed before the timer fired.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef<PendingWrite | null>(null)

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pendingRef.current = null
  }, [])

  const flushPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const p = pendingRef.current
    pendingRef.current = null
    if (p !== null) writeCommentDraft(p.userId, p.cardId, p.text)
  }, [])

  const setDraft = useCallback(
    (text: string) => {
      pendingRef.current = { userId, cardId, text }
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        const p = pendingRef.current
        pendingRef.current = null
        if (p !== null) writeCommentDraft(p.userId, p.cardId, p.text)
      }, DEBOUNCE_MS)
    },
    [userId, cardId],
  )

  const clearDraft = useCallback(() => {
    // Clear MUST win over any in-flight debounced write: drop the pending timer
    // first, then purge immediately.
    cancelPending()
    clearCommentDraft(userId, cardId)
  }, [cancelPending, userId, cardId])

  // Flush pending on identity change / unmount so a draft typed for card A
  // never lands on card B's key after a fast switch.
  useEffect(
    () => () => {
      flushPending()
    },
    [userId, cardId, flushPending],
  )

  // Best-effort refresh survival: persist before the tab is discarded / hidden.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onPageHide = () => flushPending()
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushPending()
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [flushPending])

  return { draft, setDraft, clearDraft }
}
